import { useEffect, useRef } from 'react';
import {
  createMediaTailorSession,
  type MediaTailorSession,
} from '../../lib/mediaTailor';
import type { ShakaOverlayHandle } from '../../types/app';

interface ShakaPlayerProps {
  mediaTailor?: boolean;
  onReady: (session?: MediaTailorSession) => void;
  src: string;
}

const getMimeType = (src: string): string | undefined => {
  if (/\.mpd(?:$|\?)/i.test(src)) return 'application/dash+xml';
  if (/\.m3u8(?:$|\?)/i.test(src)) return 'application/x-mpegurl';
  if (/\.mp4(?:$|\?)/i.test(src)) return 'video/mp4';
  return undefined;
};

export const ShakaPlayer = ({
  mediaTailor = false,
  onReady,
  src,
}: ShakaPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    const shaka = window.shaka;
    if (!container || !video || !shaka) return;
    let cancelled = false;
    let overlay: ShakaOverlayHandle | null = null;

    const start = async () => {
      shaka.polyfill.installAll();
      if (!shaka.Player.isBrowserSupported()) {
        throw new Error('Shaka Player is not supported in this browser');
      }

      const player = new shaka.Player();
      overlay = new shaka.ui.Overlay(player, container, video);
      player.addEventListener('error', (event) =>
        console.error('Shaka Player error', event.detail),
      );
      await player.attach(video);

      const session = mediaTailor
        ? await createMediaTailorSession()
        : undefined;
      if (cancelled) return;

      const manifestUrl = session?.manifestUrl || src;
      await player.load(manifestUrl, null, getMimeType(manifestUrl));
      if (cancelled) return;

      video.focus();
      onReady(session);
    };

    start().catch((error: unknown) => {
      if (!cancelled) console.error('Shaka Player failed to load', error);
    });

    return () => {
      cancelled = true;
      // Destroying the overlay also destroys the player it controls.
      overlay?.destroy().catch(() => undefined);
    };
  }, [mediaTailor, onReady, src]);

  return (
    <div
      ref={containerRef}
      className="player-surface h-full w-full"
      data-player-root
    >
      <video
        ref={videoRef}
        id="itg-player"
        className="h-full max-h-full w-full object-contain outline-none"
        autoPlay
        muted
        playsInline
      />
    </div>
  );
};
